import React from 'react';
import {defineMessages} from 'react-intl';
import type {ModalButtonColumnValue} from '@igp/shared/components/data-grid';
import type {DataGridEntityColDef, TextLinkCell} from '@igp/shared/components/data-grid/mui';
import {
    MuiDataGridClient,
    MuiDataGridServer,
    renderCellHeader,
    renderMuiGridModalButtonCell,
    renderTextLink,
    withDataGridEntityClient,
    withDataGridEntityServer,
} from '@igp/shared/components/data-grid/mui';
import {
    DataGridUserProfileConfigFactory as BaseDataGridUserProfileConfigFactory,
    gridUserProfileLocalizedHeaders,
    type UserProfileGridItem,
    type UserProfileViewModelKeys,
} from '@igp/shared/features/user-profile';
import {EntityType} from '@models';

import {routes} from '@config';

import {RevenueShareManagementModal} from './RevenueShareManagementModal';

const localized = defineMessages({
    details: {
        id: 'DataGridUserProfileConfigFactory_details',
        defaultMessage: 'Details',
    },
});

export class DataGridUserProfileConfigFactory extends BaseDataGridUserProfileConfigFactory {
    getColumnConfig(): Partial<Record<UserProfileViewModelKeys, DataGridEntityColDef<UserProfileViewModelKeys, UserProfileGridItem>>> {
        const columnMapping: Partial<
            Record<UserProfileViewModelKeys, DataGridEntityColDef<UserProfileViewModelKeys, UserProfileGridItem>>
        > = {
            uid_link_player_details: {
                field: 'uid_link_player_details',
                valueGetter: (_, row) => this.getUidLinkPlayerDetails(row),
                renderHeader: renderCellHeader,
                localizedHeaderName: gridUserProfileLocalizedHeaders.uid_link_player_details,
                renderCell: renderTextLink,
                sortable: false,
                flex: 0.1,
                width: 100,
            },
            uid_link_agent_details: {
                field: 'uid_link_agent_details',
                valueGetter: (_, row) => this.getUidLinkAgentDetails(row),
                renderHeader: renderCellHeader,
                localizedHeaderName: gridUserProfileLocalizedHeaders.uid_link_agent_details,
                renderCell: renderTextLink,
                sortable: false,
                flex: 0.1,
                width: 100,
            },
            p2p_status: this.getP2PStatusColumn(EntityType.P2PPlayerSettings),
            details: {
                field: 'details',
                valueGetter: (_, row) => this.getDetailsModalInfo(row),
                renderHeader: renderCellHeader,
                localizedHeaderName: localized.details,
                renderCell: renderMuiGridModalButtonCell,
                sortable: false,
                flex: 0.2,
                width: 200,
            },
        };
        return {...super.getColumnConfig(), ...columnMapping};
    }

    private getDetailsModalInfo(model: UserProfileGridItem): ModalButtonColumnValue {
        return {
            type: 'button',
            label: localized.details,
            modalContent: {
                body: <RevenueShareManagementModal agentId={model.uid} agentUsername={model.username} />,
                styleParams: {
                    maxWidth: 'lg',
                },
            },
        };
    }

    private getUidLinkPlayerDetails(model: UserProfileGridItem) {
        return {id: model?.uid, path: routes.playerDetails} as TextLinkCell;
    }

    private getUidLinkAgentDetails(model: UserProfileGridItem) {
        return {id: model?.uid, path: routes.agentDetails} as TextLinkCell;
    }
}

export const DataGridUserProfileClient = withDataGridEntityClient<UserProfileViewModelKeys, UserProfileGridItem>(MuiDataGridClient, () =>
    new DataGridUserProfileConfigFactory().getColumnConfig()
);

export const DataGridUserProfileServer = withDataGridEntityServer<UserProfileViewModelKeys, UserProfileGridItem>(MuiDataGridServer, () =>
    new DataGridUserProfileConfigFactory().getColumnConfig()
);
